// Finance Summary — executive P&L with multi-month side-by-side columns.
// Mirrors the client-provided Excel: Sales -> Payments -> Redemption
// sections. Clickable blue labels open an inline drilldown (Client,
// Order, Amount) that back-links to the Order detail page. Numbers are
// re-shaped straight from `/api/reports/branch/{bid}/finance-summary`
// so filter changes recompute instantly.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink } from "lucide-react";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";
// IMMUTABLE billing brain — rate labels on the P&L rows ("Taxes (11%)",
// "Service Charges (10%)") are DYNAMIC from the backend. See
// SYSTEM_RULES.md · "Billing Engine Isolation".
import { extractRatesFromReport, rateLabel } from "@/lib/BillingEngine";

// Clickable "blue label" configuration — every entry produces a
// drill-through link. Restricting the set here keeps the layout
// declarative and pairs with the backend `detail` payload keys.
const DRILL_KEYS = new Set([
  "discount",
  "refunds",
  "gift_card_sales",
  "series_sales",
  "gift_card_redemption",
  "series_redemption",
]);

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Numeric helper that keeps a leading minus sign for the Discount and
// Refunds rows (they read as deductions in the Sales section).
function signedMoney(branch, value, { negative = false, blank = false } = {}) {
  if (blank) return <span className="text-muted-foreground">—</span>;
  if (!value) return formatBranchMoney(branch, 0);
  return `${negative ? "- " : ""}${formatBranchMoney(branch, Math.abs(value))}`;
}

export default function FinanceSummaryReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // { key, month, rows, label }
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_month");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/finance-summary`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const months = data?.months || [];
  const { taxRatePct: taxRate, surchargeRatePct: scRate } = extractRatesFromReport(data);
  const taxRateLabel = rateLabel(taxRate);
  const scRateLabel = rateLabel(scRate);

  // Payments Type row roster = union of all method labels the branch
  // actually collected across the requested window.
  const paymentLabels = data?.payment_labels || [];

  // ---- Excel export shape (one row per section line, one column per
  // month) ------------------------------------------------------------
  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw numbers on every month column; PDF
    // preserves the branch currency label via the column's `format`.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const columns = [
      { header: "", key: "label" },
      ...months.map((m) => ({
        header: m.label, key: m.month,
        numeric: true, format: money, align: "right",
      })),
    ];
    // Signed numeric emitter: `negative:true` flips the sign so
    // SUM(Discount + Refunds) reads as a deduction naturally in Excel.
    const rowFactory = (label, key, opts = {}) => {
      const row = { label };
      months.forEach((m) => {
        const v = Number(m[key]) || 0;
        row[m.month] = opts.negative && v ? -Math.abs(v) : v;
      });
      return row;
    };
    // Section headers keep empty strings in every month column so the
    // sheet layout still visually separates SALES / PAYMENTS / REDEMPTION.
    const blankMonths = () => Object.fromEntries(months.map((m) => [m.month, ""]));
    const rows = [
      { label: "SALES", ...blankMonths() },
      rowFactory("Gross Sales", "gross_sales"),
      rowFactory("Discount", "discount", { negative: true }),
      rowFactory("Refunds", "refunds", { negative: true }),
      rowFactory("Net Sales", "net_sales"),
      rowFactory(`Taxes${taxRateLabel ? ` (${taxRateLabel})` : ""}`, "taxes"),
      rowFactory("Total Sales", "total_sales"),
      rowFactory("Gift Card Sales", "gift_card_sales"),
      rowFactory("Series Sales", "series_sales"),
      rowFactory(`Service Charges${scRateLabel ? ` (${scRateLabel})` : ""}`, "service_charges"),
      rowFactory("Total Other Sales", "total_other_sales"),
      rowFactory("Grand Total Sales", "grand_total_sales"),
      { label: "PAYMENTS TYPE", ...blankMonths() },
      ...paymentLabels.map((lbl) => ({
        label: lbl,
        ...Object.fromEntries(months.map((m) => [m.month, Number(m.payments_by_type?.[lbl]) || 0])),
      })),
      rowFactory("Total Payment", "total_payment"),
      { label: "REDEMPTION", ...blankMonths() },
      rowFactory("Gift Card Redemption", "gift_card_redemption"),
      rowFactory("Series Redemption", "series_redemption"),
      rowFactory("Total Redemption", "total_redemption"),
    ];
    return {
      filename: "finance-summary",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns,
      rows,
    };
  }, [data, months, paymentLabels, taxRateLabel, scRateLabel, branch]);

  return (
    <ReportShell
      title="Finance Summary"
      subtitle="Executive P&L layout — sales, payments and redemption reconciled side-by-side per month."
      branchName={branch?.name}
      tab="sales"
      exportSpec={exportSpec}
    >
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        {drill ? (
          <DrillDown branch={branch} bid={bid} drill={drill} onBack={() => setDrill(null)} />
        ) : (
          <Card className="rounded-lg overflow-hidden" data-testid="finance-summary-card">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                  <h2 className="text-lg font-semibold">Finance Summary</h2>
                </div>
                {data && (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Grand Total · {months.length} month{months.length === 1 ? "" : "s"}
                    </div>
                    <div className="text-xl font-semibold" data-testid="finance-grand-total-all">
                      {formatBranchMoney(branch, months.reduce((a, m) => a + (m.grand_total_sales || 0), 0))}
                    </div>
                  </div>
                )}
              </div>

              {months.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground" data-testid="finance-empty">
                  {loading ? "Loading…" : "No completed orders in this range."}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        <th className="text-left px-4 py-2 uppercase text-[10px] tracking-wider w-56">
                          &nbsp;
                        </th>
                        {months.map((m) => (
                          <th key={m.month} className="text-right px-4 py-2 uppercase text-[10px] tracking-wider min-w-32" data-testid={`finance-col-${m.month}`}>
                            {m.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* -------- SALES -------- */}
                      <SectionRow label="Sales" span={months.length + 1} />
                      <ValueRow
                        label="Gross Sales" k="gross_sales" months={months} branch={branch}
                      />
                      <ValueRow
                        label="Discount" k="discount" months={months} branch={branch}
                        negative link onClick={(m) => setDrill({ key: "discount", month: m, label: "Discount" })}
                      />
                      <ValueRow
                        label="Refunds" k="refunds" months={months} branch={branch}
                        negative link onClick={(m) => setDrill({ key: "refunds", month: m, label: "Refunds" })}
                      />
                      <ValueRow
                        label="Net Sales" k="net_sales" months={months} branch={branch}
                      />
                      <ValueRow
                        label={<span>Taxes {taxRateLabel ? <span className="text-muted-foreground">({taxRateLabel})</span> : null}</span>}
                        k="taxes" months={months} branch={branch}
                      />
                      <ValueRow
                        label="Total Sales" k="total_sales" months={months} branch={branch} strong
                      />
                      <ValueRow
                        label="Gift Card Sales" k="gift_card_sales" months={months} branch={branch}
                        link onClick={(m) => setDrill({ key: "gift_card_sales", month: m, label: "Gift Card Sales" })}
                      />
                      <ValueRow
                        label="Series Sales" k="series_sales" months={months} branch={branch}
                        link onClick={(m) => setDrill({ key: "series_sales", month: m, label: "Series Sales" })}
                      />
                      <ValueRow
                        label={<span>Service Charges {scRateLabel ? <span className="text-muted-foreground">({scRateLabel})</span> : null}</span>}
                        k="service_charges" months={months} branch={branch}
                      />
                      <ValueRow
                        label="Total Other Sales" k="total_other_sales" months={months} branch={branch}
                      />
                      <ValueRow
                        label="Grand Total Sales" k="grand_total_sales" months={months} branch={branch} strong highlight
                        testid="finance-grand-total"
                      />

                      {/* -------- PAYMENTS TYPE -------- */}
                      <SectionRow label="Payments Type" span={months.length + 1} />
                      {paymentLabels.length === 0 ? (
                        <tr className="border-b border-border">
                          <td colSpan={months.length + 1} className="px-4 py-3 text-xs text-muted-foreground italic">No payments in this window.</td>
                        </tr>
                      ) : paymentLabels.map((lbl) => (
                        <tr key={lbl} className="border-b border-border/60 hover:bg-secondary/30" data-testid={`finance-payment-${lbl.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          <td className="px-4 py-1.5 text-sm">{lbl}</td>
                          {months.map((m) => (
                            <td key={m.month} className="px-4 py-1.5 text-right text-sm">
                              {signedMoney(branch, m.payments_by_type?.[lbl])}
                            </td>
                          ))}
                        </tr>
                      ))}
                      <ValueRow
                        label="Total Payment" k="total_payment" months={months} branch={branch} strong highlight
                        testid="finance-total-payment"
                      />

                      {/* -------- REDEMPTION -------- */}
                      <SectionRow label="Redemption" span={months.length + 1} />
                      <ValueRow
                        label="Gift Card Redemption" k="gift_card_redemption" months={months} branch={branch}
                        link onClick={(m) => setDrill({ key: "gift_card_redemption", month: m, label: "Gift Card Redemption" })}
                      />
                      <ValueRow
                        label="Series Redemption" k="series_redemption" months={months} branch={branch}
                        link onClick={(m) => setDrill({ key: "series_redemption", month: m, label: "Series Redemption" })}
                      />
                      <ValueRow
                        label="Total Redemption" k="total_redemption" months={months} branch={branch} strong
                      />
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reconciliation strip — shows Grand Total vs Total Payment per
            month so the operator immediately spots a mismatch. */}
        {months.length > 0 && !drill && (
          <Card className="rounded-lg" data-testid="finance-reconcile-card">
            <CardContent className="p-4 space-y-2">
              <p className="tracking-overline text-[10px] text-muted-foreground">Reconciliation · Grand Total Sales vs Total Payment</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {months.map((m) => {
                  const delta = (m.grand_total_sales || 0) - (m.total_payment || 0);
                  const ok = Math.abs(delta) < 0.5;
                  return (
                    <div key={m.month} className={`rounded-md border p-2.5 flex items-center justify-between ${ok ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid={`finance-reconcile-${m.month}`}>
                      <div className="text-xs">
                        <div className="font-medium">{m.label}</div>
                        <div className="text-muted-foreground">
                          {ok ? "Balanced" : `Δ ${formatBranchMoney(branch, Math.abs(delta))} ${delta > 0 ? "under-collected" : "over-collected"}`}
                        </div>
                      </div>
                      <div className="text-right text-xs">
                        <div>{formatBranchMoney(branch, m.grand_total_sales)}</div>
                        <div className="text-muted-foreground">{formatBranchMoney(branch, m.total_payment)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ReportShell>
  );
}

function SectionRow({ label, span }) {
  return (
    <tr className="bg-muted border-b border-border">
      <td colSpan={span} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider">
        {label}
      </td>
    </tr>
  );
}

function ValueRow({ label, k, months, branch, negative, link, onClick, strong, highlight, testid }) {
  const drillable = link && DRILL_KEYS.has(k);
  return (
    <tr className={`border-b border-border/60 ${highlight ? "bg-secondary/50" : "hover:bg-secondary/20"}`} data-testid={testid || `finance-row-${k}`}>
      <td className={`px-4 py-1.5 text-sm ${strong ? "font-semibold" : ""}`}>
        {drillable ? (
          <span className="text-primary underline underline-offset-2 decoration-primary/40">
            {label}
          </span>
        ) : (
          label
        )}
      </td>
      {months.map((m) => {
        const value = m[k];
        const cell = signedMoney(branch, value, { negative });
        return (
          <td key={m.month} className={`px-4 py-1.5 text-right text-sm ${strong ? "font-semibold" : ""}`}>
            {drillable && value ? (
              <button
                type="button"
                onClick={() => onClick && onClick(m)}
                className="text-primary hover:underline inline-flex items-center gap-1"
                data-testid={`finance-drill-${k}-${m.month}`}
              >
                {cell}
                <ExternalLink className="w-3 h-3" />
              </button>
            ) : cell}
          </td>
        );
      })}
    </tr>
  );
}

function DrillDown({ branch, bid, drill, onBack }) {
  const { key, month, label } = drill;
  const rows = month?.detail?.[key] || [];
  const total = rows.reduce((a, r) => a + (r.amount || 0), 0);
  return (
    <div className="space-y-3" data-testid={`finance-drill-${key}`}>
      <button className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={onBack} data-testid="finance-drill-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Finance Summary
      </button>
      <Card className="rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border">
            <p className="tracking-overline text-[10px] text-muted-foreground">Detail · {month.label}</p>
            <h2 className="text-lg font-semibold">{label} · {formatBranchMoney(branch, total)}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {rows.length} transaction{rows.length === 1 ? "" : "s"}
            </p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left px-4 py-2 uppercase text-[10px] tracking-wider">When</th>
                <th className="text-left px-4 py-2 uppercase text-[10px] tracking-wider">Client Name</th>
                <th className="text-left px-4 py-2 uppercase text-[10px] tracking-wider">Order</th>
                <th className="text-right px-4 py-2 uppercase text-[10px] tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-sm text-muted-foreground py-8">
                    No transactions.
                  </td>
                </tr>
              ) : rows.map((r, i) => (
                <tr key={`${r.order_id}-${i}`} className="border-b border-border/60" data-testid={`finance-drill-row-${i}`}>
                  <td className="px-4 py-1.5 text-xs whitespace-nowrap">{fmtTime(r.at)}</td>
                  <td className="px-4 py-1.5 text-sm">
                    {r.customer_id ? (
                      <Link to={`/staff/brand/customers/${r.customer_id}`} className="text-primary hover:underline">{r.customer_name}</Link>
                    ) : r.customer_name}
                  </td>
                  <td className="px-4 py-1.5 text-xs">
                    <Link
                      to={`/staff/branch/${bid}/orders?order=${r.order_id}`}
                      className="text-primary hover:underline inline-flex items-center gap-1"
                      data-testid={`finance-drill-order-${i}`}
                    >
                      #{r.order_number} <ExternalLink className="w-3 h-3" />
                    </Link>
                  </td>
                  <td className="px-4 py-1.5 text-right font-medium">
                    {formatBranchMoney(branch, r.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
